/**
 * 換算設定の画面が扱う6つの設定項目と7テーブル。設定項目の並びは選択欄の順、見出しはプロダクト仕様書「表示項目」のとおり。
 */

export type ConversionTableName =
  | 'jpy_rates'
  | 'consumption_item_coefficients'
  | 'store_sku_base_credits'
  | 'store_sku_progressive_rates'
  | 's3_capacity_rates'
  | 'user_count_rates'
  | 'store_agent_rates';

export interface ColumnDef {
  /** 列の物理名 */
  name: string;
  header: string;
  /** 消費項目は入力しない（変更できない） */
  kind: 'number' | 'consumption_item';
}

export interface TableDef {
  name: ConversionTableName;
  label: string;
  /** 軸の列があれば先頭に置く */
  columns: ColumnDef[];
  hasAxis: boolean;
}

/** 設定項目の値（API と URL で使う）。1つのテーブルからなる設定項目は、テーブルの物理名 */
export type SettingItemKey =
  | 'jpy_rates'
  | 'consumption_item_coefficients'
  | 'store_sku_rates'
  | 's3_capacity_rates'
  | 'user_count_rates'
  | 'store_agent_rates';

export interface SettingItem {
  key: SettingItemKey;
  label: string;
  /** 同じ適用開始日の行をまとめて1つの値とするテーブル。並びは行の一覧の順 */
  tables: TableDef[];
}

const number = (name: string, header: string): ColumnDef => ({ name, header, kind: 'number' });

const conversionTables: TableDef[] = [
  {
    name: 'jpy_rates',
    label: '円換算レート',
    columns: [
      number('credit_face_value', '1クレジットの額面（円）'),
      number('jpy_usd_rate', '円/USDレート（円/USD）'),
    ],
    hasAxis: false,
  },
  {
    name: 'consumption_item_coefficients',
    label: '消費項目別の係数',
    columns: [
      { name: 'consumption_item', header: '消費項目', kind: 'consumption_item' },
      number('coefficient', '係数（倍）'),
    ],
    hasAxis: true,
  },
  {
    name: 'store_sku_base_credits',
    label: '店舗×SKUの基本クレジット',
    columns: [number('monthly_base_credit', '基本クレジット（クレジット/月）')],
    hasAxis: false,
  },
  {
    name: 'store_sku_progressive_rates',
    label: '店舗×SKUの累進テーブル',
    columns: [
      number('lower_bound', '下限（店舗×SKU）'),
      number('credit_per_unit_month', '超過分の単価（クレジット/月・店舗×SKUの1単位あたり）'),
    ],
    hasAxis: true,
  },
  {
    name: 's3_capacity_rates',
    label: 'S3容量の単価',
    columns: [number('credit_per_byte_month', '単価（クレジット/月・1TB＝10¹²バイトあたり）')],
    hasAxis: false,
  },
  {
    name: 'user_count_rates',
    label: 'ユーザー数の単価',
    columns: [number('credit_per_user_month', '単価（クレジット/月・1人あたり）')],
    hasAxis: false,
  },
  {
    name: 'store_agent_rates',
    label: 'ストアエージェントの単価',
    columns: [
      number('credit_per_seller_month', '単価（クレジット/月・1店舗あたり）'),
      number('set_multiplier', 'セット倍率（倍）'),
    ],
    hasAxis: false,
  },
];

/** 物理名から引く。7テーブルのどれでもなければ undefined。 */
export function findTable(name: string | null | undefined): TableDef | undefined {
  return conversionTables.find((t) => t.name === name);
}

const table = (name: ConversionTableName): TableDef => findTable(name)!;

const single = (name: ConversionTableName): SettingItem => ({
  key: name as SettingItemKey,
  label: table(name).label,
  tables: [table(name)],
});

export const settingItems: SettingItem[] = [
  single('jpy_rates'),
  single('consumption_item_coefficients'),
  {
    key: 'store_sku_rates',
    label: '店舗×SKUの単価',
    tables: [table('store_sku_base_credits'), table('store_sku_progressive_rates')],
  },
  single('s3_capacity_rates'),
  single('user_count_rates'),
  single('store_agent_rates'),
];

export const defaultItemKey: SettingItemKey = 'jpy_rates';

/** 設定項目の値から引く。6つの設定項目のどれでもなければ undefined。 */
export function findItem(key: string | null | undefined): SettingItem | undefined {
  return settingItems.find((i) => i.key === key);
}

/** 行を追加・削除できるのは店舗×SKUの累進テーブルだけ */
export function canAddRows(table: TableDef): boolean {
  return table.name === 'store_sku_progressive_rates';
}

export type Category = 'scheduled' | 'active' | 'past';

export const categoryLabels: Record<Category, string> = {
  scheduled: '予定',
  active: '適用中',
  past: '過去',
};
